import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { Fragment, memo, useRef, useState } from 'react';

import type { MenuElement, MenuProps } from './types';

import { Action } from '../../types/Action';
import { resolveTopLayerRoot } from '../../utils/resolveTopLayerRoot';
import { BreadcrumbLink } from '../Breadcrumb/types';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import './Menu.css';
import { MenuButtonItem, MenuLinkItem } from './subcomponents/MenuItem';

export const Menu = memo((props: MenuProps) => {
  const { as, header, label, menuItems = [], trigger } = props;
  const groupedMenuItems =
    props.as === 'button' ? props.groupedMenuItems : undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Resolved as the menu opens: the trigger is mounted by then, and its host can
  // change between openings (a dialog closing, a popover reused).
  const [portalRoot, setPortalRoot] = useState<HTMLElement | undefined>();

  const triggerRef = useRef<HTMLElement | null>(null);
  const elementsRef = useRef<Array<MenuElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const nodeId = useFloatingNodeId();
  const item = useListItem();

  const { floatingStyles, refs, context } = useFloating<HTMLElement>({
    middleware: [offset({ mainAxis: 4, alignmentAxis: 0 }), flip(), shift()],
    nodeId,
    open: isOpen,
    placement: 'bottom-start',
    // A top-layer host clips the portalled menu when it hides its own overflow;
    // fixed positioning escapes that without leaving the layer.
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    onOpenChange: (open) => {
      if (open) {
        setPortalRoot(resolveTopLayerRoot(triggerRef.current));
      }
      setIsOpen(open);
    },
  });

  const click = useClick(context, {
    event: 'mousedown',
    toggle: true,
  });
  const role = useRole(context, { role: 'menu' });
  const dismiss = useDismiss(context, { bubbles: true });
  const listNavigation = useListNavigation(context, {
    activeIndex,
    listRef: elementsRef,
    onNavigate: setActiveIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, role, dismiss, listNavigation],
  );

  const ref = useMergeRefs([refs.setReference, item.ref, triggerRef]);

  const floatingProps = getFloatingProps();

  /* `useRole` names the open menu with `aria-labelledby` pointing at the
     trigger, and `aria-labelledby` outranks `aria-label`, so an explicit name
     only takes effect once the inherited one is cleared. */
  const menuNameProps: Record<string, string | undefined> = label
    ? { 'aria-label': label, 'aria-labelledby': undefined }
    : {};

  const renderMenuItem = (item: Action | BreadcrumbLink, key: string) => {
    if (as === 'link') {
      const link = item as BreadcrumbLink;
      return <MenuLinkItem {...link} key={key} __private={{ getItemProps }} />;
    }

    const action = item as Action;

    return (
      <MenuButtonItem
        {...action}
        key={key}
        onClick={() => {
          try {
            action.onClick?.();
          } finally {
            setIsOpen(false);
          }
        }}
        __private={{ getItemProps }}
      />
    );
  };

  return (
    <>
      {trigger({ isOpen, ref, getReferenceProps })}
      <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
        {isOpen && (
          <FloatingPortal root={portalRoot}>
            <FloatingFocusManager
              context={context}
              initialFocus={0}
              modal={false}
              returnFocus={true}
            >
              <div
                className="eds-menu"
                ref={refs.setFloating}
                style={floatingStyles}
                {...floatingProps}
                {...menuNameProps}
              >
                {header && (
                  <>
                    <div className="eds-menu--header">{header}</div>
                    <div className="eds-menu--group-divider" />
                  </>
                )}
                {groupedMenuItems?.length ? (
                  groupedMenuItems.map((group, index) => (
                    <Fragment key={index}>
                      {Boolean(index > 0) && (
                        <div className="eds-menu--group-divider" />
                      )}
                      <div
                        aria-label={group.title || undefined}
                        className="eds-menu--group"
                        role={group.title ? 'group' : undefined}
                      >
                        {group.title && (
                          <Layout className="eds-menu--group-header">
                            <Text tone="subdued" variant="body-semi-m">
                              {group.title}
                            </Text>
                          </Layout>
                        )}
                        <Layout direction="column" spacing="none">
                          {group.items.map((action) =>
                            renderMenuItem(action, action.label),
                          )}
                        </Layout>
                      </div>
                    </Fragment>
                  ))
                ) : (
                  <Layout direction="column" spacing="none">
                    {menuItems.map((menuItem) =>
                      renderMenuItem(
                        menuItem,
                        as === 'link'
                          ? ((menuItem as BreadcrumbLink).text ?? '')
                          : (menuItem as Action).label,
                      ),
                    )}
                  </Layout>
                )}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingList>
    </>
  );
});

Menu.displayName = 'Menu';
